import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, AlertCircle, Shield } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { apiClient, ApiError } from '../lib/api';

export function PasswordVerificationPage() {
  const { slug } = useParams<{ slug: string }>();
  
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Auto-focus password input when page loads
    const input = document.getElementById('password-input');
    if (input) {
      input.focus();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password.trim() || !slug) {
      setError('Password is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Verify password with the API
      const response = await apiClient.verifyLinkPassword(slug, { password });
      
      if (response.success && response.data?.destination) {
        // Redirect to the actual destination
        window.location.href = response.data.destination;
      } else {
        setError('Invalid password. Please try again.');
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError('Invalid password. Please try again.');
        } else if (err.status === 404) {
          setError('Link not found or has expired.');
        } else if (err.status === 429) {
          setError('Too many attempts. Please wait a moment and try again.');
        } else {
          setError(err.message || 'An error occurred. Please try again.');
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    // Redirect to marketing page instead of dashboard
    // Determine marketing URL based on current environment
    const currentHost = window.location.host;
    let marketingUrl = 'https://go2url.me'; // Default production URL
    
    if (currentHost.includes('localhost')) {
      marketingUrl = 'http://localhost:4321'; // Local Astro dev server
    } else if (currentHost.includes('go2url.dev')) {
      marketingUrl = 'https://www.go2url.dev'; // Local development domain
    }
    
    window.location.href = marketingUrl;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/20">
            <Shield className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            Password Protected Link
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            This link is password protected. Please enter the password to continue.
          </p>
          {slug && (
            <div className="mt-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                <Lock className="w-3 h-3 mr-1" />
                {slug}
              </span>
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg px-8 py-6">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="password-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </Label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  disabled={isLoading}
                  required
                  autoFocus
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleGoBack}
                disabled={isLoading}
                className="flex-1"
              >
                Go Back
              </Button>
              <Button
                type="submit"
                disabled={!password.trim() || isLoading}
                className="flex-1"
              >
                {isLoading ? 'Verifying...' : 'Access Link'}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              If you don't have the password, please contact the person who shared this link with you.
            </p>
          </div>
        </div>

        {/* Branding footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Powered by{' '}
            <a 
              href="/" 
              className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500"
            >
              go2url.me
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}